import { ActionType, createAction, createAsyncAction } from 'typesafe-actions';
import { DefaultErrorPayload, FetchAction, SuccessFetchActionPayload } from '../dataFetching/generators';
import { DataFetchingThunkAction } from '../types';
import { ProductLooksResponseData } from './types';
import { log404ErrorAsWarning } from 'Common/apiAdapters/ErrorHandlers';
import GaTracking from 'Common/ga';
import { GenericLaunchDarklyTracker } from 'Client/LaunchDarklyTracker';

export const setLooksInfusionSliderViewAction = createAction('@PRODUCT_LOOKS/VIEW_SUCCESS');

export const resetLooksInfusionSliderViewAction = createAction('@PRODUCT_LOOKS/VIEW_RESET');

export const fetchProductLooksActions = createAsyncAction(
    '@PRODUCT_LOOKS/FETCH_START',
    '@PRODUCT_LOOKS/FETCH_SUCCESS',
    '@PRODUCT_LOOKS/FETCH_FAILED'
)<
    FetchAction,
    SuccessFetchActionPayload<
        ProductLooksResponseData,
        {
            sku: string;
            countryCode?: string;
            currency?: string;
            ga?: GaTracking;
            launchDarklyTracker: GenericLaunchDarklyTracker;
        }
    >,
    DefaultErrorPayload
>();

export const WRONG_API_RESPONSE_ERROR = 'Data from the API does not match the the shape of the products looks response';

export function isProductLooksResponse(data: ProductLooksResponseData): data is ProductLooksResponseData {
    const productLook = data?.looks;

    const matchesProductLooksResponseConditions = !!(
        Array.isArray(productLook) &&
        Array.isArray(productLook[0].products) &&
        typeof productLook[0].slug === 'string' &&
        typeof productLook[0].caption === 'string' &&
        typeof productLook[0].isLandscape === 'boolean' &&
        typeof productLook[0].trackingName === 'string' &&
        typeof productLook[0].portraitListingImage !== 'undefined'
    );

    return matchesProductLooksResponseConditions;
}

type FetchProductLooksParams = {
    sku?: string;
    countryCode?: string;
    currency?: string;
    limit?: number;
    launchDarklyTracker: GenericLaunchDarklyTracker;
};

export const fetchProductLooks = ({
    sku = '',
    countryCode,
    currency,
    launchDarklyTracker,
    limit = 1,
}: FetchProductLooksParams): DataFetchingThunkAction => async (dispatch, getState, { apiService, ga, logger }) => {
    const identifier = sku;

    if (identifier === getState().productLooks.identifier) {
        return;
    }

    dispatch(fetchProductLooksActions.request({ identifier }));

    try {
        const { data, success, errors } = await apiService.shopApi.getProductLooksBySku(sku, limit);

        if (success) {
            if (isProductLooksResponse(data)) {
                dispatch(
                    fetchProductLooksActions.success({
                        data,
                        identifier,
                        reducerPayloadOptions: { sku, countryCode, currency, ga, launchDarklyTracker },
                    })
                );
            } else {
                logger.error(WRONG_API_RESPONSE_ERROR, { action: 'fetchProductLooks' });
                dispatch(fetchProductLooksActions.failure({ error: WRONG_API_RESPONSE_ERROR, identifier }));
            }
        }

        if (errors.length > 0) {
            dispatch(fetchProductLooksActions.failure({ error: JSON.stringify(errors), identifier }));
        }
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchProductLooks');
        dispatch(fetchProductLooksActions.failure({ error: err, identifier }));
    }
};

export const ProductLooksActions = [
    fetchProductLooksActions.failure,
    fetchProductLooksActions.request,
    fetchProductLooksActions.success,
    setLooksInfusionSliderViewAction,
    resetLooksInfusionSliderViewAction,
];

export type ProductLooksActionsType =
    | ActionType<typeof fetchProductLooksActions.failure>
    | ActionType<typeof fetchProductLooksActions.request>
    | ActionType<typeof fetchProductLooksActions.success>
    | ActionType<typeof setLooksInfusionSliderViewAction>
    | ActionType<typeof resetLooksInfusionSliderViewAction>;
